import {
  Asset,
  AssetLabel,
  AssetLabelSummary,
  GetAssetResponse,
  LabelingStatus,
  ListFolderContentsResponse,
} from '@/api/generatedApi';
import { GET_ASSET_QUERY_KEY } from '@/hooks/api/assets/useGetAsset';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { FOLDER_LABEL_OPTIONS_QUERY_KEY } from '@/hooks/api/folders/useGetFolderLabelOptions';
import { FOLDER_COLUMN_VISIBILITY_QUERY_KEY } from '@/hooks/api/folders/useFolderColumnVisibility';
import { QueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

type AssetLabelUpdater = (existingLabel: AssetLabel | undefined) => AssetLabel | undefined;

const findAssetInFolderCaches = (queryClient: QueryClient, assetId: string): Asset | undefined => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });

  for (const query of queries) {
    const data = queryClient.getQueryData<AxiosResponse<ListFolderContentsResponse>>(query.queryKey);
    if (!data) continue;
    const match = data.data.contents.find(item => item.contentType === 'asset' && item.id === assetId);
    if (match?.contentType === 'asset') return match;
  }
  return undefined;
};

const updateContents = <T extends ListFolderContentsResponse>(
  response: AxiosResponse<T>,
  assetId: string,
  updater: AssetLabelUpdater
): AxiosResponse<T> => {
  const contents = response.data.contents.map(item => {
    if (item.contentType !== 'asset' || item.id !== assetId) return item;
    const nextLabel = updater(item.label);
    return { ...item, label: nextLabel };
  });
  return { ...response, data: { ...response.data, contents } };
};

const synthesizeGetResponse = (asset: Asset, updater: AssetLabelUpdater): AxiosResponse<GetAssetResponse> => ({
  data: { asset: { ...asset, label: updater(asset.label) } },
  status: 200,
  statusText: 'OK',
  headers: {},
  config: {} as AxiosResponse['config'],
});

export const optimisticallyUpdateAssetLabel = ({
  queryClient,
  assetId,
  tenantId,
  updater,
  fallbackAsset,
}: {
  queryClient: QueryClient;
  assetId: string;
  tenantId: string;
  updater: AssetLabelUpdater;
  fallbackAsset?: Asset;
}): void => {
  queryClient.setQueriesData<AxiosResponse<ListFolderContentsResponse>>(
    { queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false },
    previous => (previous ? updateContents(previous, assetId, updater) : previous)
  );

  const baseAsset = fallbackAsset ?? findAssetInFolderCaches(queryClient, assetId);

  queryClient.setQueryData<AxiosResponse<GetAssetResponse>>([GET_ASSET_QUERY_KEY, { assetId, tenantId }], previous => {
    if (previous) {
      const nextLabel = updater(previous.data.asset.label);
      return {
        ...previous,
        data: { ...previous.data, asset: { ...previous.data.asset, label: nextLabel } },
      };
    }
    if (baseAsset) {
      return synthesizeGetResponse(baseAsset, updater);
    }
    return previous;
  });
};

const clearFolderLabelQueries = (queryClient: QueryClient): void => {
  queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
  queryClient.resetQueries({ queryKey: [FOLDER_COLUMN_VISIBILITY_QUERY_KEY], exact: false });
  queryClient.invalidateQueries({ queryKey: [FOLDER_LABEL_OPTIONS_QUERY_KEY], exact: false });
};

export const clearLabelQueries = ({
  queryClient,
  assetId,
  tenantId,
}: {
  queryClient: QueryClient;
  assetId: string;
  tenantId: string;
}): void => {
  queryClient.invalidateQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId, tenantId }] });
  clearFolderLabelQueries(queryClient);
};

export const clearAllLabelQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: [GET_ASSET_QUERY_KEY], exact: false });
  clearFolderLabelQueries(queryClient);
};

export const labelPendingUpdater: AssetLabelUpdater = () => ({ status: LabelingStatus.Pending });

// eslint-disable-next-line unicorn/no-useless-undefined -- AssetLabelUpdater requires returning AssetLabel | undefined
export const labelClearedUpdater: AssetLabelUpdater = () => undefined;

export const labelAssignedUpdater =
  (summary: AssetLabelSummary): AssetLabelUpdater =>
  () => ({ status: LabelingStatus.Assigned, summary });
